import type {
  ClaimsCategory,
  ClaimsSubcategory,
  ClaimsProgress,
  ClaimsVariation,
} from "../types/database.types";

// Month names for display
export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Get month name from number (1-12)
export const getMonthName = (month: number): string => {
  return MONTH_NAMES[month - 1] || "";
};

// Default categories for new projects
export const DEFAULT_CATEGORIES = ["COLUMNS", "WALLS", "DECK", "SCREENS"];

// Default subcategories (levels) for each category
export const DEFAULT_SUBCATEGORIES = [
  "Basement 4",
  "Basement 3",
  "Basement 2",
  "Basement 1",
  "Ground",
  "Level 2",
  "Level 3",
  "Level 4",
  "Level 5",
  "Level 6",
  "Level 7",
  "Level 8",
  "Level 9",
  "Level 10",
  "Level 11",
  "Level 12",
  "Level 13",
  "Level 14",
  "Level 15",
  "Roof",
];

// ─── Safe Mode (Employee Presentation Privacy) ──────────────────────────────
// Module-level flag checked by all currency formatters. When active, all
// dollar values render as "•••••" to protect sensitive financial data
// during screen-sharing or presentations.
let _safeModeActive = false;

export function setSafeModeActive(active: boolean): void {
  _safeModeActive = active;
}

export function isSafeModeActive(): boolean {
  return _safeModeActive;
}

const SAFE_MODE_MASK = "•••••";
const SAFE_MODE_MASK_COMPACT = "•••";

// Currency formatter for AUD
export const formatCurrency = (amount: number | undefined | null, includeDecimals: boolean = false): string => {
  if (_safeModeActive) return SAFE_MODE_MASK;

  const safeAmount = amount ?? 0;
  if (isNaN(safeAmount)) return "$0";

  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: includeDecimals ? 2 : 0,
    maximumFractionDigits: includeDecimals ? 2 : 0,
  }).format(safeAmount);
};

// Compact currency formatter: $1.5M, $140K, $850
export const formatCompactCurrency = (amount: number | undefined | null): string => {
  if (_safeModeActive) return SAFE_MODE_MASK_COMPACT;

  const val = amount ?? 0;
  if (isNaN(val)) return "$0";

  const abs = Math.abs(val);
  const sign = val < 0 ? "-" : "";

  if (abs >= 1_000_000_000) {
    const b = abs / 1_000_000_000;
    return `${sign}$${b % 1 === 0 ? b.toFixed(0) : b.toFixed(1)}B`;
  }
  if (abs >= 1_000_000) {
    const m = abs / 1_000_000;
    return `${sign}$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    const k = abs / 1_000;
    return `${sign}$${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}K`;
  }
  return `${sign}$${abs % 1 === 0 ? abs.toFixed(0) : abs.toFixed(2)}`;
};

// Compact axis tick formatter for Recharts: same logic, suitable for tickFormatter prop
export const compactCurrencyTick = (val: number): string => {
  if (_safeModeActive) return "";
  return formatCompactCurrency(val);
};

// Percentage formatter
export const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

// Calculate contract value from categories and subcategories
export const calculateContractValue = (
  categories: (ClaimsCategory & { subcategories?: ClaimsSubcategory[] })[],
): number => {
  return categories.reduce((total, category) => {
    if (category.is_custom_amount_only && category.custom_amount) {
      return total + Number(category.custom_amount);
    }

    const subcategoryTotal = (category.subcategories || []).reduce(
      (sum, sub) => sum + Number(sub.amount || 0),
      0,
    );

    return total + subcategoryTotal;
  }, 0);
};

// Auto-detect input type and calculate complementary value
export interface ClaimCalculation {
  percentage: number;
  amount: number;
  inputType: "percentage" | "amount";
}

export const calculateProgressClaim = (
  inputValue: number,
  subcategoryAmount: number,
  inputType?: "percentage" | "amount",
): ClaimCalculation => {
  // If input type is specified, use it
  if (inputType === "percentage") {
    return {
      percentage: inputValue, // Allow negative and >100% values
      amount: (subcategoryAmount * inputValue) / 100,
      inputType: "percentage",
    };
  }

  if (inputType === "amount") {
    return {
      percentage:
        subcategoryAmount > 0 ? (inputValue / subcategoryAmount) * 100 : 0,
      amount: inputValue,
      inputType: "amount",
    };
  }

  // Auto-detect: if value is between 0-100 and subcategory amount is > 1000, likely percentage
  if (inputValue >= 0 && inputValue <= 100 && subcategoryAmount > 1000) {
    return {
      percentage: inputValue,
      amount: (subcategoryAmount * inputValue) / 100,
      inputType: "percentage",
    };
  }

  // Otherwise treat as amount
  return {
    percentage:
      subcategoryAmount > 0 ? (inputValue / subcategoryAmount) * 100 : 0,
    amount: inputValue,
    inputType: "amount",
  };
};

// Calculate variation total with full Saturday labour split
// Contract Saturday uses saturdayContractOTRate (OT only, Normal removed in v27)
// Non-Contract uses saturdayRate
export const calculateVariationTotal = (
  hoursNormal: number,
  hoursOT: number,
  satContractOT: number,
  satNonContractQty: number,
  materialCost: number,
  normalRate: number,
  overtimeRate: number,
  saturdayRate: number,
  saturdayContractOTRate: number,
): number => {
  return (
    hoursNormal * normalRate +
    hoursOT * overtimeRate +
    satContractOT * saturdayContractOTRate +
    satNonContractQty * saturdayRate +
    materialCost
  );
};

// Calculate waiting/approved amounts
export interface VariationApproval {
  approved: number;
  waiting: number;
}

export const calculateVariationApproval = (
  total: number,
  approvedAmount?: number,
  waitingAmount?: number,
): VariationApproval => {
  if (approvedAmount !== undefined && approvedAmount >= 0) {
    return {
      approved: approvedAmount,
      waiting: Math.max(0, total - approvedAmount),
    };
  }

  if (waitingAmount !== undefined && waitingAmount >= 0) {
    return {
      approved: Math.max(0, total - waitingAmount),
      waiting: waitingAmount,
    };
  }

  // Default: all waiting
  return {
    approved: 0,
    waiting: total,
  };
};

// Get RAG status for budget tracking
export type RAGStatus = "green" | "amber" | "red";

export const getRAGStatus = (claimedPercentage: number): RAGStatus => {
  if (claimedPercentage > 100) return "red";
  if (claimedPercentage > 90) return "amber";
  return "green";
};

// Calculate monthly summary
export interface MonthlySummary {
  year: number;
  month: number;
  totalMonthlyContractValue: number;
  variationsThisMonth: number;
  variationsOutstanding: number;
  totalClaimThisMonth: number;
}

export const calculateMonthlySummary = (
  year: number,
  month: number,
  progressClaims: ClaimsProgress[],
  variations: ClaimsVariation[],
): MonthlySummary => {
  const monthProgress = progressClaims.filter(
    (p) => p.claim_year === year && p.claim_month === month,
  );

  const monthVariations = variations.filter(
    (v) => v.claim_year === year && v.claim_month === month,
  );

  const totalMonthlyContractValue = monthProgress.reduce(
    (sum, p) => sum + Number(p.amount || 0),
    0,
  );

  const variationsThisMonth = monthVariations.reduce(
    (sum, v) => sum + Number(v.approved_amount || 0),
    0,
  );

  const variationsOutstanding = monthVariations.reduce(
    (sum, v) => sum + Number(v.waiting_amount || 0),
    0,
  );

  return {
    year,
    month,
    totalMonthlyContractValue,
    variationsThisMonth,
    variationsOutstanding,
    totalClaimThisMonth:
      totalMonthlyContractValue + variationsThisMonth + variationsOutstanding,
  };
};

// Calculate total progress for a subcategory across all months
export const calculateTotalProgress = (
  subcategoryId: string,
  subcategoryAmount: number,
  progressClaims: ClaimsProgress[],
): { totalPercentage: number; totalAmount: number; ragStatus: RAGStatus } => {
  const claims = progressClaims.filter(
    (p) => p.subcategory_id === subcategoryId,
  );

  const totalAmount = claims.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const totalPercentage =
    subcategoryAmount > 0 ? (totalAmount / subcategoryAmount) * 100 : 0;

  return {
    totalPercentage,
    totalAmount,
    ragStatus: getRAGStatus(totalPercentage),
  };
};

// Calculate variations approved total (revised contract value)
export const calculateRevisedContractValue = (
  contractValue: number,
  variations: ClaimsVariation[],
): number => {
  const approvedVariations = variations.reduce(
    (sum, v) => sum + Number(v.approved_amount || 0),
    0,
  );
  return contractValue + approvedVariations;
};
// Generate month/year options for selectors
export const generateMonthOptions = (
  startYear = 2023,
  endYear = 2030,
): { year: number; month: number; label: string }[] => {
  const options: { year: number; month: number; label: string }[] = [];

  for (let year = startYear; year <= endYear; year++) {
    for (let month = 1; month <= 12; month++) {
      options.push({
        year,
        month,
        label: `${getMonthName(month)} ${year}`,
      });
    }
  }

  return options;
};
